// src/pages/markets/board/card/HoverPanel.tsx
//
// The panel is a frame, not a result. It opens on the mouseenter that produced
// it — before any request is made — and the content decides what to draw while
// it waits. The old version looked slow because the panel's *content* was the
// thing being awaited: nothing rendered until the traders arrived, so a 2s
// request read as a 2s dead hover.
//
// `content` is a function, not a node, on purpose: passing a node means the
// subtree (and its queries) is built by the parent on every render, whether the
// panel is open or not. As a function it mounts on open and unmounts on close,
// which is what gives the request a lifetime to be aborted against.
//
// `minWidth` / `minHeight` reserve the panel's final size so the loading state
// and the loaded state are the same rectangle — a panel that resizes under the
// cursor takes its own content out from under the pointer.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Popper } from '@mui/material';
import type { PopperPlacementType } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { panelFrameSx } from './panelParts';

export interface HoverPanelProps {
    content: React.ReactNode | (() => React.ReactNode);
    placement?: PopperPlacementType;
    disabled?: boolean;
    children: React.ReactNode;
    sx?: React.ComponentProps<typeof Box>['sx'];
    style?: React.CSSProperties;
    padded?: boolean;
    /** Dwell required before opening. 0 for a target the reader aims at; a
     *  small delay for one of many, so a crossing cursor opens nothing. */
    openDelayMs?: number;
    /** On the trigger, so the hover target and the click target are one element. */
    onClick?: (e: React.MouseEvent) => void;
    testId?: string;
    // Reserved panel size, so open-empty and open-loaded are one rectangle.
    minWidth?: number;
    minHeight?: number;
}

const CLOSE_DELAY_MS = 120;

export function HoverPanel({
    content,
    placement = 'right-start',
    disabled = false,
    children,
    sx,
    style,
    padded = true,
    openDelayMs = 0,
    onClick,
    testId,
    minWidth,
    minHeight,
}: HoverPanelProps) {
    const theme = useTheme();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const timer = useRef<number | null>(null);
    const openTimer = useRef<number | null>(null);

    const cancelClose = useCallback(() => {
        if (timer.current !== null) {
            window.clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    const cancelOpen = useCallback(() => {
        if (openTimer.current !== null) {
            window.clearTimeout(openTimer.current);
            openTimer.current = null;
        }
    }, []);

    // The panel is cheap; the requests behind it are what cost. With one
    // target the reader aims at, open at once. With many — the entry dots on a
    // chart — a cursor crossing them would otherwise fetch a trader profile
    // and a position for every dot it passes, which is what wedges a scroll.
    const open = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            if (disabled) return;
            cancelClose();
            const el = e.currentTarget;
            if (openDelayMs <= 0) {
                setAnchor(el);
                return;
            }
            cancelOpen();
            openTimer.current = window.setTimeout(
                () => setAnchor(el),
                openDelayMs
            );
        },
        [disabled, cancelClose, cancelOpen, openDelayMs]
    );

    const scheduleClose = useCallback(() => {
        cancelOpen();
        cancelClose();
        timer.current = window.setTimeout(
            () => setAnchor(null),
            CLOSE_DELAY_MS
        );
    }, [cancelClose, cancelOpen]);

    useEffect(
        () => () => {
            cancelClose();
            cancelOpen();
        },
        [cancelClose, cancelOpen]
    );

    const isOpen = Boolean(anchor);

    return (
        <>
            <Box
                data-testid={testId}
                onMouseEnter={open}
                onMouseLeave={scheduleClose}
                onClick={onClick}
                sx={sx}
                style={style}
            >
                {children}
            </Box>
            {/* keepMounted stays off: unmounting the subtree is what cancels
                the in-flight request when the cursor leaves. */}
            <Popper
                open={isOpen}
                anchorEl={anchor}
                placement={placement}
                modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                sx={{ zIndex: theme.zIndex.modal }}
            >
                <Paper
                    elevation={0}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    sx={{
                        p: padded ? 1.5 : 0,
                        maxWidth: 680,
                        minWidth,
                        minHeight,
                        ...panelFrameSx,
                    }}
                >
                    {isOpen
                        ? typeof content === 'function'
                            ? content()
                            : content
                        : null}
                </Paper>
            </Popper>
        </>
    );
}
